/**
 * TasksView — Core embeddable task list with status tabs and type filter
 *
 * Built on `SDataTable` (the project's canonical table primitive) in the
 * "always-on selection" variant — bulk operations are this table's primary job,
 * so checkboxes are permanently visible and there is no `[Select]` mode flip.
 * See the Storybook playbook `Dialog Playbook/13 - Data Tables`, Pattern 1, and
 * `src/modules/product/components/TasksTable.tsx` for the reference consumer.
 *
 * Accepts an external TaskFilter that gets merged with tab/type selection.
 */

import React, { useState, useMemo, useCallback, createElement } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Tabs,
  Tab,
  Badge,
  Typography,
  Chip,
  Stack,
  IconButton,
  Tooltip,
  Alert,
  useMediaQuery,
  type SvgIconProps,
} from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import CircleIcon from '@mui/icons-material/Circle';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
// Subpath import: the ui-components barrel transitively pulls firebase-core
// (import.meta.env) and breaks jest for every consumer. Playbook §Import Path.
import {
  SDataTable,
  useSelectionMode,
  SelectionActionsBar,
  type SDataTableColumnDef,
} from '@modules/ui-components/dialogs/SDataTable';
import { SDialog } from '@modules/ui-components/dialogs/SDialog';
import { useTasks } from '../hooks/useTasks';
import { useTaskActions } from '../hooks/useTaskActions';
import { useTaskStore } from '../store/taskStore';
import { TaskTypeFilter } from './TaskTypeFilter';
import { TaskDetailDialog } from './TaskDetailDialog';
import type { TaskResultActionsRenderer } from './TaskDetailDialog';
import { TASK_TYPE_CONFIG, STATUS_CHIP_COLORS, STATUS_LABELS } from '../constants/taskTypeConfig';
import { TASKS_PANEL } from '../selectors/tasks-panel.sel';
import {
  formatTimestamp,
  formatMoneyMinor,
  formatDurationMs,
  getTaskDurationMs,
} from '../utils/taskFormatters';
import type { TaskFilter, TaskStatus, BackendTask, TaskRowActionsRenderer } from '../types';

type TabKey = 'inProgress' | 'done' | 'failed' | 'cancelled' | 'all';

const TAB_STATUS_MAP: Record<TabKey, TaskStatus[] | undefined> = {
  inProgress: ['pending', 'running', 'retrying', 'cancelling'],
  done: ['completed'],
  failed: ['error'],
  cancelled: ['cancelled', 'skipped'],
  all: undefined,
};

const TAB_LABELS: Record<TabKey, string> = {
  inProgress: 'In Progress',
  done: 'Done',
  failed: 'Failed',
  cancelled: 'Cancelled',
  all: 'All',
};

const TAB_ORDER: TabKey[] = ['inProgress', 'done', 'failed', 'cancelled', 'all'];

/**
 * Statuses whose worker may still write to the doc. The server refuses to delete
 * these (the next status write would resurrect the document), so their rows are
 * not selectable — a checkbox that always fails is worse than no checkbox.
 */
const NON_DELETABLE_STATUSES: readonly string[] = ['pending', 'running', 'retrying', 'cancelling'];

const isDeletable = (task: BackendTask): boolean =>
  !NON_DELETABLE_STATUSES.includes(task.status);

/** "1 task" / "3 tasks" — `N task(s)` reads like a placeholder that shipped. */
const pluralizeTasks = (count: number): string => `${count} ${count === 1 ? 'task' : 'tasks'}`;

/** `billing` is written on the Firestore doc but is not on the BackendTask type. */
const getBillingMinor = (task: BackendTask): number | undefined =>
  (task as unknown as { billing?: { creditDebitMinor?: number } }).billing?.creditDebitMinor;

const getBillingCurrency = (task: BackendTask): string | undefined =>
  (task as unknown as { billing?: { currency?: string } }).billing?.currency;

export interface TasksViewProps {
  /** External filter merged with tab selection */
  filter?: TaskFilter;
  /** Hide tab bar (useful when embedding with fixed filter) */
  hideTabs?: boolean;
  /** Hide task type filter */
  hideTypeFilter?: boolean;
  /** Optional module-specific actions rendered inside TaskDetailDialog. */
  renderResultActions?: TaskResultActionsRenderer;
  /** Optional module-specific actions rendered directly in task rows. */
  renderRowActions?: TaskRowActionsRenderer;
}

export const TasksView: React.FC<TasksViewProps> = ({
  filter,
  hideTabs = false,
  hideTypeFilter = false,
  renderResultActions,
  renderRowActions,
}) => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<TabKey>('all');
  const [selectedTaskTypes, setSelectedTaskTypes] = useState<string[]>([]);
  const [detailTaskId, setDetailTaskId] = useState<string | null>(null);

  const currentUserId = useTaskStore((s) => s.currentUserId);
  const { markViewed, deleteTasks } = useTaskActions();

  // Narrow viewports: drop the secondary numerics instead of scrolling the table
  // sideways. Task / Type / Status / Started stay — they are what the history is
  // read for. The threshold is the table's own width (columns ≈ 1060px + padding),
  // not an MUI breakpoint: at exactly 900px `down('md')` is false and the Actions
  // column would fall off the right edge.
  const isNarrow = useMediaQuery('(max-width:1100px)');
  const columnVisibility = useMemo(
    () => ({ credits: !isNarrow, duration: !isNarrow, userId: !isNarrow }),
    [isNarrow]
  );

  // Selection: always-on variant — the hook owns state, the table never flips
  // `selectionMode` (checkbox and row-click coexist). Playbook §"Always-on selection".
  const selection = useSelectionMode({ defaultMode: true });

  const [pendingDeleteIds, setPendingDeleteIds] = useState<string[] | null>(null);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [deleteNotice, setDeleteNotice] = useState<
    { severity: 'success' | 'warning' | 'error'; message: string } | null
  >(null);

  // Build merged filter: external filter + tab status + selected task types
  const mergedFilter = useMemo<TaskFilter>(() => {
    const tabStatuses = TAB_STATUS_MAP[activeTab];
    return {
      ...filter,
      // Tab status overrides external filter's status/statusMode
      ...(tabStatuses ? { status: tabStatuses, statusMode: undefined } : { statusMode: 'all' as const }),
      // Task type filter (only if user selected specific types)
      ...(selectedTaskTypes.length > 0 ? { taskType: selectedTaskTypes } : {}),
      // Always sort newest first — note: store sort logic is inverted, 'asc' gives desc order
      sortBy: 'createdAt' as const,
      sortDirection: 'asc' as const,
    };
  }, [filter, activeTab, selectedTaskTypes]);

  // Filtered tasks for current tab
  const tasks = useTasks(mergedFilter);

  // All tasks (unfiltered by tab) for counting per-tab and extracting available types
  const allTasks = useTasks({
    ...filter,
    statusMode: 'all',
    sortBy: 'createdAt' as const,
    sortDirection: 'asc' as const,
  });

  // Tab counts
  const tabCounts = useMemo(() => {
    const counts: Record<TabKey, number> = {
      inProgress: 0,
      done: 0,
      failed: 0,
      cancelled: 0,
      all: allTasks.length,
    };
    for (const task of allTasks) {
      for (const tabKey of TAB_ORDER) {
        const statuses = TAB_STATUS_MAP[tabKey];
        if (statuses?.includes(task.status)) {
          counts[tabKey]++;
        }
      }
    }
    return counts;
  }, [allTasks]);

  // Available task types (from all tasks, not just current tab)
  const availableTypes = useMemo(() => {
    const types = new Set<string>();
    for (const task of allTasks) {
      if (task.taskType) types.add(task.taskType);
    }
    return Array.from(types).sort();
  }, [allTasks]);

  const isUnviewed = useCallback(
    (task: BackendTask) => {
      if (!currentUserId) return false;
      return !task.viewedBy?.includes(currentUserId);
    },
    [currentUserId]
  );

  const openDetail = useCallback(
    (task: BackendTask) => {
      // Mark as viewed when opening detail
      if (currentUserId && !task.viewedBy?.includes(currentUserId)) {
        markViewed(task.id);
      }
      setDetailTaskId(task.id);
    },
    [currentUserId, markViewed]
  );

  const handleNavigate = useCallback(
    (task: BackendTask) => {
      if (task.targetEntityPath?.frontendUrl) {
        navigate(task.targetEntityPath.frontendUrl);
      }
    },
    [navigate]
  );

  const handleDetailNavigate = useCallback(
    (frontendUrl: string) => {
      navigate(frontendUrl);
      setDetailTaskId(null);
    },
    [navigate]
  );

  const runDelete = useCallback(async () => {
    if (!pendingDeleteIds || pendingDeleteIds.length === 0) return;
    setDeleteBusy(true);
    try {
      const outcome = await deleteTasks(pendingDeleteIds);

      if (outcome.error) {
        setDeleteNotice({ severity: 'error', message: `Delete failed: ${outcome.error}` });
      } else {
        // Honest reporting: never claim a clean sweep when the server refused some.
        const parts = [`Deleted ${pluralizeTasks(outcome.deleted.length)}`];
        const activeSkipped = outcome.skipped.filter((s) => s.reason === 'active').length;
        if (activeSkipped > 0) parts.push(`${activeSkipped} still running and were kept`);
        if (outcome.failed.length > 0) parts.push(`${outcome.failed.length} failed`);
        setDeleteNotice({
          severity: outcome.failed.length > 0 ? 'error' : activeSkipped > 0 ? 'warning' : 'success',
          message: parts.join(' · '),
        });
      }

      selection.clearSelection();
      setPendingDeleteIds(null);
    } finally {
      setDeleteBusy(false);
    }
  }, [pendingDeleteIds, deleteTasks, selection]);

  const columns = useMemo<SDataTableColumnDef<BackendTask>[]>(
    () => [
      {
        accessorKey: 'action',
        header: 'Task',
        size: 320,
        Cell: ({ row }) => {
          const task = row.original;
          const config = TASK_TYPE_CONFIG[task.taskType ?? ''];
          const unviewed = isUnviewed(task);
          return (
            <Stack direction="row" alignItems="center" gap={1.25} sx={{ minWidth: 0 }}>
              <Box sx={{ flexShrink: 0, display: 'flex', alignItems: 'center', width: 20 }}>
                {config?.icon
                  ? createElement(config.icon as React.ComponentType<SvgIconProps>, {
                      sx: { fontSize: 20, color: 'text.secondary' },
                    })
                  : null}
              </Box>
              {unviewed && (
                <CircleIcon sx={{ fontSize: 8, color: 'primary.main', flexShrink: 0 }} />
              )}
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="body2" noWrap sx={{ fontWeight: unviewed ? 600 : 400 }}>
                  {task.action}
                </Typography>
                {task.description && (
                  <Typography variant="caption" color="text.secondary" noWrap display="block">
                    {task.description}
                  </Typography>
                )}
              </Box>
            </Stack>
          );
        },
      },
      {
        id: 'taskType',
        header: 'Type',
        size: 130,
        accessorFn: (task) => TASK_TYPE_CONFIG[task.taskType ?? '']?.label ?? task.taskType ?? 'Task',
        Cell: ({ cell }) => (
          <Chip
            label={cell.getValue<string>()}
            size="small"
            variant="outlined"
            sx={{ height: 22, fontSize: '0.7rem' }}
          />
        ),
      },
      {
        id: 'status',
        header: 'Status',
        size: 100,
        accessorFn: (task) => STATUS_LABELS[task.status] ?? task.status,
        Cell: ({ row, cell }) => (
          <Chip
            label={cell.getValue<string>()}
            size="small"
            color={STATUS_CHIP_COLORS[row.original.status] ?? 'default'}
            sx={{ height: 22, fontSize: '0.7rem', minWidth: 70 }}
            data-testid={`${TASKS_PANEL.statusPrefix}${row.original.id}`}
          />
        ),
      },
      {
        id: 'credits',
        header: 'Credits',
        size: 90,
        accessorFn: (task) => getBillingMinor(task) ?? 0,
        muiTableHeadCellProps: { align: 'right' },
        muiTableBodyCellProps: { align: 'right' },
        Cell: ({ row }) => {
          const minor = getBillingMinor(row.original);
          return (
            <Typography
              variant="caption"
              color={(minor ?? 0) > 0 ? 'error.main' : 'text.secondary'}
            >
              {formatMoneyMinor(minor, getBillingCurrency(row.original))}
            </Typography>
          );
        },
      },
      {
        id: 'createdAt',
        header: 'Started',
        size: 105,
        accessorFn: (task) => (task.createdAt instanceof Date ? task.createdAt.getTime() : 0),
        Cell: ({ row }) => (
          <Typography variant="caption" color="text.secondary">
            {formatTimestamp(row.original.createdAt)}
          </Typography>
        ),
      },
      {
        id: 'duration',
        header: 'Duration',
        size: 110,
        accessorFn: (task) => getTaskDurationMs(task.createdAt, task.updatedAt) ?? -1,
        muiTableHeadCellProps: { align: 'right' },
        muiTableBodyCellProps: { align: 'right' },
        Cell: ({ row }) => (
          <Typography variant="caption" color="text.disabled">
            {formatDurationMs(getTaskDurationMs(row.original.createdAt, row.original.updatedAt))}
          </Typography>
        ),
      },
      {
        accessorKey: 'userId',
        header: 'By',
        size: 100,
        Cell: ({ cell }) => (
          <Typography variant="caption" color="text.secondary" noWrap>
            {cell.getValue<string>() || 'unknown'}
          </Typography>
        ),
      },
    ],
    [isUnviewed]
  );

  const selectedCount = selection.selectedIds.length;

  return (
    <Box
      sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}
      data-testid={TASKS_PANEL.tasksView}
    >
      {/* Tabs */}
      {!hideTabs && (
        <Tabs
          value={activeTab}
          onChange={(_e, val: TabKey) => setActiveTab(val)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}
          data-testid={TASKS_PANEL.tasksViewTabs}
        >
          {TAB_ORDER.map((key) => (
            <Tab
              key={key}
              value={key}
              label={
                <Badge
                  badgeContent={tabCounts[key]}
                  color="default"
                  max={999}
                  sx={{ '& .MuiBadge-badge': { fontSize: '0.65rem', height: 16, minWidth: 16 } }}
                >
                  <span>{TAB_LABELS[key]}</span>
                </Badge>
              }
              data-testid={`${TASKS_PANEL.tasksViewTabPrefix}${key}`}
              sx={{ textTransform: 'none', minHeight: 48 }}
            />
          ))}
        </Tabs>
      )}

      {/* Toolbar: type filter + bulk-selection actions. The section header owns the
          bulk surface — SDataTable's own top toolbar stays disabled. */}
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        flexWrap="wrap"
        gap={1}
        sx={{ px: 2, py: 1 }}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          {!hideTypeFilter && availableTypes.length > 1 && (
            <TaskTypeFilter
              availableTypes={availableTypes}
              selectedTypes={selectedTaskTypes}
              onChange={setSelectedTaskTypes}
            />
          )}
        </Box>
        <SelectionActionsBar
          selection={selection}
          busy={deleteBusy}
          deleteLabel={`Delete ${pluralizeTasks(selectedCount)}`}
          deleteTestId={TASKS_PANEL.tasksViewDeleteSelected}
          onDeleteSelected={(ids) => setPendingDeleteIds(ids)}
        />
      </Stack>

      {deleteNotice && (
        <Alert
          severity={deleteNotice.severity}
          onClose={() => setDeleteNotice(null)}
          sx={{ mx: 2, mb: 1 }}
          data-testid={TASKS_PANEL.tasksViewDeleteResult}
        >
          {deleteNotice.message}
        </Alert>
      )}

      {/* Task table */}
      <Box sx={{ flex: 1, overflow: 'auto', px: 2, pb: 2 }}>
        {tasks.length === 0 ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              No tasks {activeTab !== 'all' ? `with status "${TAB_LABELS[activeTab]}"` : 'found'}
            </Typography>
          </Box>
        ) : (
          <SDataTable<BackendTask>
            columns={columns}
            data={tasks}
            getRowId={(task) => task.id}
            // Bulk delete is this table's primary job → checkboxes always visible.
            // Active tasks are excluded: the server refuses them anyway.
            enableRowSelection={(row) => isDeletable(row.original)}
            enableRowActions
            enableTopToolbar={false}
            // MRT's default 'semantic' layout sizes columns by content, so a raw
            // 28-char userId pushed the Actions column off-screen. 'grid' honours
            // the per-column `size` values below.
            layoutMode="grid"
            enableSearch
            searchInColumn="action"
            searchMode="global"
            searchPlaceholder="Search tasks…"
            // Opt-in required: SDataTable locks pagination off by default, and this
            // history can hold thousands of rows.
            enablePagination
            initialState={{
              pagination: { pageIndex: 0, pageSize: 50 },
              sorting: [{ id: 'createdAt', desc: true }],
            }}
            state={{ rowSelection: selection.rowSelection, columnVisibility }}
            onRowSelectionChange={selection.setRowSelection}
            muiTableBodyRowProps={({ row }) => ({
              hover: true,
              onClick: () => openDetail(row.original),
              sx: {
                cursor: 'pointer',
                ...(isUnviewed(row.original) && { bgcolor: 'action.hover' }),
              },
              // Preserved contract: live e2e specs click this testid.
              'data-testid': `${TASKS_PANEL.tasksViewRowPrefix}${row.original.id}`,
            })}
            positionActionsColumn="last"
            renderRowActions={({ row }) => (
              <Stack direction="row" alignItems="center" gap={0.25}>
                {row.original.targetEntityPath?.frontendUrl && (
                  <Tooltip title="Open result">
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleNavigate(row.original);
                      }}
                      data-testid={`${TASKS_PANEL.navigatePrefix}${row.original.id}`}
                    >
                      <OpenInNewIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Tooltip>
                )}
                {isDeletable(row.original) && (
                  <Tooltip title="Delete task">
                    <IconButton
                      size="small"
                      color="error"
                      onClick={(e) => {
                        e.stopPropagation();
                        setPendingDeleteIds([row.original.id]);
                      }}
                      data-testid={`${TASKS_PANEL.tasksViewRowDeletePrefix}${row.original.id}`}
                    >
                      <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Tooltip>
                )}
                {/* Domain-owned extension slot — background-tasks stays generic. */}
                {renderRowActions?.(row.original)}
              </Stack>
            )}
          />
        )}
      </Box>

      {/* Destructive confirm — caller-owned per the playbook. No header X: the
          footer carries an explicit Cancel (ui-components invariants 2 and 3). */}
      <SDialog
        open={pendingDeleteIds !== null}
        onClose={() => {
          if (!deleteBusy) setPendingDeleteIds(null);
        }}
        title={`Delete ${pluralizeTasks(pendingDeleteIds?.length ?? 0)}?`}
        maxWidth="xs"
        fullWidth
        showCloseButton={false}
        data-testid={TASKS_PANEL.tasksViewDeleteConfirm}
        secondaryAction={{
          label: 'Cancel',
          onClick: () => setPendingDeleteIds(null),
          disabled: deleteBusy,
        }}
        primaryAction={{
          label: `Delete ${pluralizeTasks(pendingDeleteIds?.length ?? 0)}`,
          onClick: runDelete,
          color: 'error',
          loading: deleteBusy,
          'data-testid': TASKS_PANEL.tasksViewDeleteConfirmSubmit,
        }}
      >
        <Typography variant="body2">
          {pluralizeTasks(pendingDeleteIds?.length ?? 0)} will be permanently removed from this
          project&apos;s history.
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
          Their AI debug payloads (prompts, raw model responses, phase metadata) are deleted
          too. This cannot be undone.
        </Typography>
      </SDialog>

      {/* Detail dialog */}
      <TaskDetailDialog
        taskId={detailTaskId}
        open={detailTaskId !== null}
        onClose={() => setDetailTaskId(null)}
        onNavigate={handleDetailNavigate}
        renderResultActions={renderResultActions}
      />
    </Box>
  );
};
